import { useCallback, useState } from 'react';
import { Upload, X, FileText, Image, File } from 'lucide-react';
import { clsx } from 'clsx';

interface FileUploadProps {
  accept?: string;
  maxSize?: number;
  onFileSelect: (file: File | null) => void;
  selectedFile?: File | null;
  label?: string;
}

function getFileIcon(type: string) {
  if (type.startsWith('image/')) return Image;
  if (type === 'application/pdf') return FileText;
  return File;
}

export function FileUpload({
  accept = 'application/pdf,image/jpeg,image/png',
  maxSize = 10 * 1024 * 1024,
  onFileSelect,
  selectedFile,
  label = 'Arrastra tu archivo aquí o haz clic para seleccionar',
}: FileUploadProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validateFile = useCallback(
    (file: File): boolean => {
      setError(null);

      const acceptedTypes = accept.split(',').map((t) => t.trim());
      if (!acceptedTypes.includes(file.type)) {
        setError('Tipo de archivo no permitido. Solo PDF, JPG o PNG.');
        return false;
      }

      if (file.size > maxSize) {
        setError(`El archivo excede el tamaño máximo de ${maxSize / 1024 / 1024}MB.`);
        return false;
      }

      return true;
    },
    [accept, maxSize]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragging(false);

      const file = e.dataTransfer.files[0];
      if (file && validateFile(file)) {
        onFileSelect(file);
      }
    },
    [validateFile, onFileSelect]
  );

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file && validateFile(file)) {
        onFileSelect(file);
      }
    },
    [validateFile, onFileSelect]
  );

  const handleRemove = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onFileSelect(null);
    },
    [onFileSelect]
  );

  if (selectedFile) {
    const FileIcon = getFileIcon(selectedFile.type);

    return (
      <div className="w-full">
        {label && (
          <p className="text-sm font-medium text-gray-700 mb-2">{label}</p>
        )}
        <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <FileIcon className="w-8 h-8 text-gray-400 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">
              {selectedFile.name}
            </p>
            <p className="text-xs text-gray-500">
              {(selectedFile.size / 1024).toFixed(1)} KB
            </p>
          </div>
          <button
            onClick={handleRemove}
            className="p-1.5 rounded-full hover:bg-gray-200 transition-colors"
            aria-label="Eliminar archivo"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {label && (
        <p className="text-sm font-medium text-gray-700 mb-2">{label}</p>
      )}
      <div
        onDrop={handleDrop}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        className={clsx(
          'relative border-2 border-dashed rounded-xl p-8 transition-all duration-200 text-center',
          isDragging
            ? 'border-primary-400 bg-primary-50'
            : 'border-gray-300 hover:border-primary-300 hover:bg-gray-50'
        )}
      >
        <input
          type="file"
          accept={accept}
          onChange={handleFileInput}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          aria-label="Seleccionar archivo"
        />
        <div className="flex flex-col items-center gap-3">
          <div
            className={clsx(
              'p-3 rounded-full transition-colors',
              isDragging ? 'bg-primary-100' : 'bg-gray-100'
            )}
          >
            <Upload
              className={clsx(
                'w-6 h-6 transition-colors',
                isDragging ? 'text-primary-600' : 'text-gray-400'
              )}
            />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-900">
              Arrastra tu archivo aquí
            </p>
            <p className="text-xs text-gray-500 mt-1">
              o haz clic para seleccionar
            </p>
          </div>
          <p className="text-xs text-gray-400">
            PDF, JPG, PNG • Max {maxSize / 1024 / 1024}MB
          </p>
        </div>
      </div>
      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
    </div>
  );
}
